import { gathrxrAuthHeader } from '../utils/quotaHeaders';

// OpenRouter API — image generation + prompt assistance
//   Avatars / NPCs:    Meta Muse Image (~$0.01), Nano Banana 2 as fallback
//   Objects:           Meta Muse Image, Nano Banana Pro as fallback
//   Photo restyles:    Nano Banana 2 (needs an input picture)
//   Prompt assist:     a fast text model that rewrites a short idea into a
//                      detailed image prompt

// Always the same-origin Pages Function: the key lives server-side only. In
// dev, Vite proxies /api to the live functions (see vite.config.ts), so no
// provider key is ever read by client code (and none can be inlined).
const OPENROUTER_URL = '/api/openrouter/chat';
const MODEL_ID = 'google/gemini-3.1-flash-image-preview';        // Nano Banana 2
export const MUSE_IMAGE = 'meta/muse-image';                      // Meta Muse Image, ~$0.01
// Image-only models are served on OpenRouter's /images endpoint, not chat.
const IMAGES_URL = '/api/openrouter/images';
export const NANO_BANANA_PRO = 'google/gemini-3-pro-image-preview'; // Nano Banana Pro
const PROMPT_ASSIST_MODEL = 'google/gemini-2.5-flash';

function authHeaders(): Record<string, string> {
  return {}; // key lives server-side; auth goes in gathrxrAuthHeader()
}

export interface GeneratedImage {
  dataUrl: string;   // data:image/png;base64,...
  text?: string;     // optional text response
}

/**
 * Core image request — shared by character and object generation.
 * @param model  OpenRouter image model slug
 * @param imageSize '1K' | '2K' (Pro supports 2K/4K)
 */
async function requestImage(prompt: string, model: string, imageSize: '1K' | '2K' = '1K', signal?: AbortSignal): Promise<GeneratedImage> {
  const res = await fetch(OPENROUTER_URL, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(await gathrxrAuthHeader()),
    },
    body: JSON.stringify({
      model,
      modalities: ['image', 'text'],
      messages: [{ role: 'user', content: prompt }],
      image_config: { aspect_ratio: '1:1', image_size: imageSize },
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `OpenRouter request failed: ${res.status}`);
  }

  const data = await res.json();
  const choice = data.choices?.[0]?.message;

  if (!choice) throw new Error('No response from image model');

  // Extract image from response — could be in images array or inline in content
  let imageUrl: string | undefined;

  // Check images array first (standard format)
  if (choice.images?.length > 0) {
    imageUrl = choice.images[0].image_url?.url || choice.images[0].url;
  }

  // Some responses embed base64 in content parts
  if (!imageUrl && Array.isArray(choice.content)) {
    for (const part of choice.content) {
      if (part.type === 'image_url') {
        imageUrl = part.image_url?.url;
        break;
      }
    }
  }

  // Check if content itself is a base64 string
  if (!imageUrl && typeof choice.content === 'string' && choice.content.startsWith('data:image')) {
    imageUrl = choice.content;
  }

  if (!imageUrl) {
    throw new Error('No image was generated. Try a different prompt.');
  }

  return {
    dataUrl: imageUrl,
    text: typeof choice.content === 'string' ? choice.content : undefined,
  };
}

/** Re-encode a picture as a JPEG data URL, at most `max` px a side (Muse sends WebP; Meshy wants JPG/PNG). */
async function toJpeg(src: string, max = 1280): Promise<string> {
  const img = new Image();
  img.src = src;
  await img.decode();
  const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(img.naturalWidth * k);
  c.height = Math.round(img.naturalHeight * k);
  const g = c.getContext('2d');
  if (!g) throw new Error('Could not read the generated image');
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, c.width, c.height);
  g.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.92);
}

/** One picture from an image-only model (OpenRouter /images), as it came (data URL or https URL). */
async function rawImagesEndpoint(prompt: string, model: string, signal?: AbortSignal, size?: string): Promise<string> {
  const res = await fetch(IMAGES_URL, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(await gathrxrAuthHeader()),
    },
    body: JSON.stringify({ model, prompt, n: 1, ...(size ? { size } : {}) }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error?.message || err.message || `Image request failed: ${res.status}`);
    (e as any).quota = !!err.quota; // the caller's own allowance / sign-in, see functions/api/_quota.ts
    throw e;
  }
  const data = await res.json();
  const item = data.data?.[0];
  const raw: string | undefined = item?.b64_json
    ? `data:${item.media_type || 'image/webp'};base64,${item.b64_json}`
    : item?.url;
  if (!raw) throw new Error('No image was generated. Try a different prompt.');
  return raw;
}

/** One picture from an image-only model (OpenRouter /images). Returns a JPEG data URL. */
async function requestImagesEndpoint(prompt: string, model: string, signal?: AbortSignal): Promise<GeneratedImage> {
  return { dataUrl: await toJpeg(await rawImagesEndpoint(prompt, model, signal)) };
}

/** Muse at full size (a 2:1 prompt gives 2240×1120), untouched — for skyboxes. */
export function generateMuseRaw(prompt: string, size?: string, signal?: AbortSignal): Promise<string> {
  return rawImagesEndpoint(prompt, MUSE_IMAGE, signal, size);
}

/**
 * Muse first; if it fails for any reason other than the caller's own limits
 * (signed out, allowance used up, cancelled), retry on a Gemini model.
 */
async function museThen(prompt: string, fallback: () => Promise<GeneratedImage>, signal?: AbortSignal): Promise<GeneratedImage> {
  try {
    return await requestImagesEndpoint(prompt, MUSE_IMAGE, signal);
  } catch (err: any) {
    if (signal?.aborted || err?.quota) throw err;
    console.warn('[images] Muse failed, using Gemini:', err?.message);
    return fallback();
  }
}

/**
 * Generate a full-body character reference image from a text prompt
 * (Muse Image, Nano Banana 2 as fallback). Returns a JPEG/PNG data URL.
 */
export async function generateCharacterImage(prompt: string, signal?: AbortSignal): Promise<GeneratedImage> {
  return museThen(prompt, () => requestImage(prompt, MODEL_ID, '1K', signal), signal);
}

/**
 * Generate a single 3D-ready object image (Muse Image, Nano Banana Pro as fallback).
 * Wraps the user's idea with framing that suits Meshy image-to-3D:
 * one centered subject, plain background, even lighting, no scene clutter.
 */
export async function generateObjectImage(idea: string): Promise<GeneratedImage> {
  const prompt = [
    `A single 3D object: ${idea.trim()}.`,
    'Centered, full object visible, three-quarter front view.',
    'Plain solid light-gray background, soft even studio lighting, no shadows on the ground,',
    'no other objects, no text, no people. Photorealistic, high detail,',
    'suitable as a reference for photogrammetry / image-to-3D conversion.',
  ].join(' ');
  return museThen(prompt, () => requestImage(prompt, NANO_BANANA_PRO, '2K'));
}

/** A whole scene, for Studio's "From a picture" when there's no photo yet:
 *  an eye-level shot with clear, separate furniture and props. */
export async function generateSceneImage(idea: string, signal?: AbortSignal): Promise<GeneratedImage> {
  const prompt = [
    `An eye-level photo of ${idea.trim()}.`,
    'Taken standing at the entrance, the whole space in view, every piece of furniture and decor clearly separate and fully visible,',
    'nothing cut off at the edges, no people, natural even light, photorealistic, sharp.',
  ].join(' ');
  return museThen(prompt, () => requestImage(prompt, NANO_BANANA_PRO, '2K', signal), signal);
}

/**
 * Studio "From a picture": redraw ONE object from a scene photo on its own,
 * whole and on a plain background, ready for image-to-3D. Both the full photo
 * (context, lighting, hidden parts) and a close crop (which object) go in.
 */
const FLAT_RATIOS: [string, number][] = [['21:9', 21 / 9], ['16:9', 16 / 9], ['3:2', 1.5], ['4:3', 4 / 3], ['5:4', 1.25], ['1:1', 1], ['4:5', 0.8], ['3:4', 0.75], ['2:3', 2 / 3], ['9:16', 9 / 16]];
/** The supported aspect ratio closest to width:height. */
const nearestRatio = (w: number, h: number) =>
  FLAT_RATIOS.reduce((best, r) => (Math.abs(Math.log(r[1] * h / w)) < Math.abs(Math.log(best[1] * h / w)) ? r : best))[0];

/**
 * `flat` (wall art, rugs, mirrors): instead of a product shot, the object's
 * face seen straight on and filling the whole picture, at its own aspect
 * ratio (width:height), to be used as the texture of a thin panel.
 */
export async function isolateObjectImage(
  photo: string, crop: string, name: string, description: string, signal?: AbortSignal,
  flat?: { width: number; height: number },
): Promise<GeneratedImage> {
  const instruction = flat ? [
    `Image 1 is a photo of a scene. Image 2 is a close-up crop of one flat object in it: the ${name}${description ? ` (${description})` : ''}.`,
    'Show ONLY its face, perfectly flat and seen straight on, with the perspective removed, filling the entire image edge to edge.',
    'Keep its exact design, colours and pattern; fill in any parts hidden behind other things. No background, no wall, no floor, no shadow, no border around it.',
  ].join(' ') : [
    `Image 1 is a photo of a scene. Image 2 is a close-up crop of one object in it: the ${name}${description ? ` (${description})` : ''}.`,
    'Draw ONLY that object, complete and whole, as a clean product shot: the same object, colours, materials and proportions, seen from the same angle as in the photo.',
    'Fill in any parts hidden behind other things. Remove everything else: no other furniture, nothing on it or leaning against it, no floor, rug or wall.',
    'Centre it on a plain light grey background with soft even light and no cast shadow. The whole object must be in frame.',
  ].join(' ');
  const res = await fetch(OPENROUTER_URL, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({
      model: MODEL_ID,
      modalities: ['image', 'text'],
      messages: [{
        role: 'user',
        content: [
          { type: 'image_url', image_url: { url: photo } },
          { type: 'image_url', image_url: { url: crop } },
          { type: 'text', text: instruction },
        ],
      }],
      image_config: { aspect_ratio: flat ? nearestRatio(flat.width, flat.height) : '1:1', image_size: '1K' },
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Image request failed: ${res.status}`);
  }
  const choice = (await res.json()).choices?.[0]?.message;
  let imageUrl: string | undefined = choice?.images?.[0]?.image_url?.url || choice?.images?.[0]?.url;
  if (!imageUrl && Array.isArray(choice?.content)) imageUrl = choice.content.find((p: any) => p.type === 'image_url')?.image_url?.url;
  if (!imageUrl) throw new Error(`Couldn't redraw the ${name}.`);
  return { dataUrl: imageUrl };
}

/**
 * Prompt assistance — expand a short user idea into a richer, more specific
 * image prompt. Best-effort: on any failure returns the original idea so the
 * pipeline never blocks on it.
 */
export async function enhancePrompt(idea: string, kind: 'object' | 'character' = 'object'): Promise<string> {
  const trimmed = idea.trim();
  if (!trimmed) return trimmed;
  try {
    const system = kind === 'object'
      ? 'You rewrite a user\'s short idea into ONE vivid, concrete sentence describing a single physical object for an image generator: material, color, shape, era/style, distinctive details. No preamble, no lists, no quotes — output only the rewritten description.'
      : 'You rewrite a user\'s short idea into ONE vivid sentence describing a character\'s appearance for an image generator: build, clothing, colors, distinctive features. No preamble, no lists, no quotes — output only the description.';
    const res = await fetch(OPENROUTER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...(await gathrxrAuthHeader()),
      },
      body: JSON.stringify({
        model: PROMPT_ASSIST_MODEL,
        messages: [
          { role: 'system', content: system },
          { role: 'user', content: trimmed },
        ],
        max_tokens: 160,
        temperature: 0.8,
      }),
    });
    if (!res.ok) return trimmed;
    const data = await res.json();
    const text: string | undefined = data.choices?.[0]?.message?.content;
    const cleaned = text?.trim().replace(/^["']|["']$/g, '');
    return cleaned && cleaned.length > 0 ? cleaned : trimmed;
  } catch {
    return trimmed;
  }
}

const STUDIO_ENHANCE_MODEL = 'google/gemini-3.1-flash-lite'; // ~$0.0005 per enhance

/**
 * Studio: expand a short idea into a rich brief for a 3D world or model.
 * Unlike enhancePrompt this throws, since the user asked for it and should
 * see when it didn't work.
 */
export async function enhanceStudioPrompt(idea: string, kind: 'world' | 'model'): Promise<string> {
  const trimmed = idea.trim();
  if (!trimmed) throw new Error('Write an idea first.');
  const system = kind === 'world'
    ? 'You expand a user\'s idea into a brief for an AI that builds a walkable 3D environment (a room, venue or small outdoor block, under 20 m across). Write 3-4 sentences, max 600 characters: the layout and what sits where, the key furniture/objects (with counts), materials and colors, lighting and time of day, and the mood. Keep everything the user asked for and do not change their intent. Do not name a rendering style (that is chosen separately). Plain prose only: no preamble, lists, headings or quotes.'
    : 'You expand a user\'s idea into a brief for an AI that builds a single 3D model (a prop, piece of furniture or object). Write 2-3 sentences, max 400 characters: overall shape and proportions, real-world size, materials and colors, and distinctive details. Keep everything the user asked for and do not change their intent. Do not name a rendering style. Plain prose only: no preamble, lists, headings or quotes.';
  const res = await fetch(OPENROUTER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({
      model: STUDIO_ENHANCE_MODEL,
      messages: [{ role: 'system', content: system }, { role: 'user', content: trimmed }],
      max_tokens: 260,
      temperature: 0.7,
    }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error?.message || `Enhance failed (${res.status})`);
  const text: string | undefined = data?.choices?.[0]?.message?.content;
  const cleaned = text?.trim().replace(/^["']|["']$/g, '');
  if (!cleaned) throw new Error('Enhance returned nothing. Try again.');
  return cleaned.slice(0, 800);
}

/**
 * Build a detailed T-pose character prompt from user selections.
 */
export function buildCharacterPrompt(opts: {
  style: string;
  bodyType: string;
  gender: string;
  skinTone: string;
  hairStyle: string;
  hairColor: string;
  clothing: string;
  extras: string;
}): string {
  const parts = [
    `Generate a full-body character in a perfect T-pose (arms straight out to the sides, legs together, facing forward)`,
    `${opts.style.toLowerCase()} art style`,
    `${opts.gender.toLowerCase()} character`,
    `${opts.bodyType.toLowerCase()} body type`,
    `${opts.skinTone.toLowerCase()} skin tone`,
    `${opts.hairStyle.toLowerCase()} ${opts.hairColor.toLowerCase()} hair`,
    `wearing ${opts.clothing.toLowerCase()}`,
  ];
  if (opts.extras.trim()) parts.push(opts.extras.trim());
  parts.push('centered on a plain white background, full body visible from head to feet, high quality, clean lines, suitable for 3D model reference');
  return parts.join(', ');
}

/**
 * Restyle an existing character: same person, new outfit.
 *
 * This is the step Looks/retexture cannot do — retexture repaints the existing
 * geometry, so a t-shirt stays t-shirt-shaped whatever it is painted as. Here
 * the reference image carries the identity and the model redraws the person in
 * new clothes; the result then goes through the normal image→3D→rig pipeline,
 * producing genuinely different garment geometry as a sibling avatar.
 *
 * Identity lives in the image conditioning rather than in prompt pleading,
 * which held up far better in practice than the retexture guard did.
 */
export async function restyleCharacterImage(
  referenceImageUrl: string,
  outfit: string,
  opts: { hair?: string; makeup?: string } = {},
): Promise<GeneratedImage> {
  const wants: string[] = [`Change the outfit to: ${outfit.trim()}.`];
  if (opts.hair?.trim()) wants.push(`Change the hair colour to ${opts.hair.trim()}, same hairstyle.`);
  if (opts.makeup?.trim()) wants.push(`Apply makeup: ${opts.makeup.trim()}.`);

  // The 3D build copies this picture, so any drift in the face here becomes a
  // different face on the avatar. Lock identity explicitly and feature by
  // feature — a generic "same face" let the model lighten the skin, change the
  // eye colour and drop the makeup.
  const instruction = [
    'Redraw this exact character wearing different clothing. ONLY the clothing changes.',
    'The face must be identical to the reference: same skin tone and undertone (do not lighten or darken it),',
    'same eye shape and eye colour, same eyebrows, same nose, same lips and lip colour, same jawline,',
    (opts.makeup?.trim() ? '' : 'same makeup, ') + 'same face shape, same age. Same body proportions' +
      (opts.hair?.trim() ? '.' : ', same hairstyle and hair colour.'),
    'Keep accessories such as earrings and necklaces unless the outfit replaces them.',
    ...wants,
    'Full body from head to toe, T-pose with arms straight out to the sides, facing forward,',
    'the character fills the frame height, the face sharp and well lit,',
    'plain solid light-gray background, even lighting, no text, single character only.',
  ].join(' ');

  const res = await fetch(OPENROUTER_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(await gathrxrAuthHeader()),
    },
    body: JSON.stringify({
      model: MODEL_ID,
      modalities: ['image', 'text'],
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: instruction },
          { type: 'image_url', image_url: { url: referenceImageUrl } },
        ],
      }],
      image_config: { aspect_ratio: '2:3', image_size: '1K' }, // portrait: more pixels on the face
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `OpenRouter request failed: ${res.status}`);
  }

  const data = await res.json();
  const choice = data.choices?.[0]?.message;
  if (!choice) throw new Error('No response from image model');

  let imageUrl: string | undefined;
  if (choice.images?.length > 0) imageUrl = choice.images[0].image_url?.url || choice.images[0].url;
  if (!imageUrl && Array.isArray(choice.content)) {
    for (const part of choice.content) {
      if (part.type === 'image_url') { imageUrl = part.image_url?.url; break; }
    }
  }
  if (!imageUrl && typeof choice.content === 'string' && choice.content.startsWith('data:image')) {
    imageUrl = choice.content;
  }
  if (!imageUrl) throw new Error('No image was generated. Try a different description.');

  return { dataUrl: imageUrl, text: typeof choice.content === 'string' ? choice.content : undefined };
}

/**
 * "Make it look like me": draw the person in a selfie as a full-body character
 * in the art style of an onboarding style card. Both images go to the model —
 * the selfie for identity, the card for style, outfit and pose — so the result
 * matches the card the user picked instead of a text description of it.
 */
export async function stylizeSelfie(
  selfieDataUrl: string,
  styleImageDataUrl: string,
  styleName: string,
): Promise<GeneratedImage> {
  const instruction = [
    'Image 1 is a photo of a person. Image 2 is a character in a specific art style.',
    `Draw the person from image 1 as a full-body character in the exact "${styleName}" art style, rendering and proportions of image 2.`,
    'Keep their face, identity, skin tone and hairstyle recognisable. A similar outfit to image 2 is fine.',
    'Full body head to toe, relaxed A-pose facing forward, arms slightly away from the body, empty hands,',
    'plain solid light-gray background, even lighting, no text, single character only.',
  ].join(' ');

  const res = await fetch(OPENROUTER_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(await gathrxrAuthHeader()),
    },
    body: JSON.stringify({
      model: MODEL_ID,
      modalities: ['image', 'text'],
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: instruction },
          { type: 'image_url', image_url: { url: selfieDataUrl } },
          { type: 'image_url', image_url: { url: styleImageDataUrl } },
        ],
      }],
      image_config: { aspect_ratio: '2:3', image_size: '1K' },
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `OpenRouter request failed: ${res.status}`);
  }
  const data = await res.json();
  const choice = data.choices?.[0]?.message;
  if (!choice) throw new Error('No response from image model');
  let imageUrl: string | undefined;
  if (choice.images?.length > 0) imageUrl = choice.images[0].image_url?.url || choice.images[0].url;
  if (!imageUrl && Array.isArray(choice.content)) {
    for (const part of choice.content) {
      if (part.type === 'image_url') { imageUrl = part.image_url?.url; break; }
    }
  }
  if (!imageUrl && typeof choice.content === 'string' && choice.content.startsWith('data:image')) {
    imageUrl = choice.content;
  }
  if (!imageUrl) throw new Error('No image came back. Try a clearer photo.');
  return { dataUrl: imageUrl };
}
